import type { Option } from "@tento-lona/sheets";
import type { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import type { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import type { RenderPlan } from "@tento-lona/sheets/platform";
import { CellGroup } from "@tento-ui/components/sheet/cell-group";
import type { CellBinding } from "./default-binder-helpers.ts";
import { cellStructureMatches } from "./cell-structure.ts";
import type { CursorEvidence } from "./commit-referee.ts";

/**
 * What a bound `SheetCell` shows, as the binder's keep / skip gates and its
 * referees read it: which row and column it displays, the last full rebind it
 * completed, and its output fingerprint. Pure functions over the cell; the
 * binder (`DefaultBinder`) re-exports them under its namespace.
 */

/** A cell's last completed full rebind (N3-3): what a membership keep must match. */
export type CompletedBind = {
  readonly epoch: number;
  readonly rowId: string;
  readonly inThePast: boolean;
  readonly isCurrent: boolean;
};

/**
 * Whether `$cell` already displays `column` (its last bind positioned it
 * there). A skip / no-op reuse of a cell is sound ONLY when this holds — the
 * cell pool is recycled across columns, so a row-only match can leave a
 * recycled cell showing a DIFFERENT date's content (the stale-weekend /
 * "column doesn't repaint on horizontal scroll" bug). `cellId.col` is the
 * column the PREVIOUS bind positioned this cell at (`setId` runs inside the
 * rebind/reflow effects, never in `applyStructure`, so at skip-decision time
 * the occupant still carries its last bind's column). Compared by SEMANTIC
 * column identity — the date via `PartialDate.equals` AND the timezone via
 * `tz.fullname` (the IANA name; the exact pair `Resolved.Default.toString()`
 * keys the column on, `pd + "_" + tz.fullname`) — never `===` on the interned
 * column, never the anchor-relative `offset`. The timezone axis is
 * load-bearing: switching the display timezone (personal ⇄ secondary) rebuilds
 * every column with the SAME date `pd` but a DIFFERENT `tz`, so a pd-only match
 * would authorize a stale skip and leave column widths/content unreset. A
 * `null` cellId (never positioned) reads as a different column → not skippable.
 */
export function cellDisplaysColumn(
  $cell: Pick<SheetCell, "cellId">,
  column: ColumnIndex.Key.Resolved.Default,
): boolean {
  const col = $cell.cellId?.col ?? null;
  return col != null &&
    col.pd.equals(column.pd) &&
    col.tz.fullname === column.tz.fullname;
}

/** Content preservation requires the row and semantic column already on DOM. */
export function cellDisplaysRowAndColumn(
  $cell: Pick<SheetCell, "cellId" | "getAttribute">,
  rowId: string,
  column: ColumnIndex.Key.Resolved.Default,
): boolean {
  return $cell.getAttribute("rid") === rowId && cellDisplaysColumn($cell, column);
}

/**
 * A cheap output-identity fingerprint of a `SheetCell` — the LR6 referee's
 * ground truth (row-identity ledger, L5). Captures the structural facts a
 * would-skip decision claims are unchanged: which plugin content type is
 * mounted, whether the cell is hidden, its bound height, and whether it holds a
 * content element. Deliberately excludes `boundGeneration` (stamped every bind)
 * and per-plugin value state: a would-skip means the row's output `data`/`attr`
 * versions are unchanged, so the plugin value is identical — the fingerprint's
 * job is to catch a STRUCTURAL change (content rebuilt, height, visibility) that
 * happened despite an unchanged output version, which is exactly a divergence.
 */
export interface CellFingerprint {
  readonly contentType: Option<string>;
  readonly hidden: boolean;
  readonly height: Option<number>;
  readonly hasContent: boolean;
}

/**
 * The dark per-cell referee: a classified skip/resize/span cell's pre-rebind
 * capture, pending its post-rebind fingerprint. `pre` is `null` when the cell
 * was not LAYER-2 sampled this pass; `cursor` is the always-on
 * LAYER-1 ledger verdict (used only for skips).
 */
export interface PendingCellSample {
  readonly reflowHonorable: boolean;
  readonly index: number;
  readonly pre: Option<CellFingerprint>;
  readonly cursor: CursorEvidence;
}

/** Snapshot a cell's output fingerprint (LR6 referee). */
export function fingerprint($cell: SheetCell): CellFingerprint {
  return {
    contentType: $cell.contentType ?? null,
    hidden: $cell.hasAttribute("hidden"),
    height: $cell.height ?? null,
    hasContent: $cell.$content != null,
  };
}

/** Whether a cell's output fingerprint moved across a bind (LR6 referee). */
export function fingerprintChanged(
  before: CellFingerprint,
  after: CellFingerprint,
): boolean {
  return (
    before.contentType !== after.contentType ||
    before.hidden !== after.hidden ||
    before.height !== after.height ||
    before.hasContent !== after.hasContent
  );
}

/** Shared live-skip and observe-only precondition; row/column/height stay separate. */
export function cellStructureHonorable(
  $cell: SheetCell,
  plan: RenderPlan<ColumnIndex.Key.Resolved.Default>,
  binding: Option<CellBinding>,
): boolean {
  return cellStructureMatches({
    hidden: $cell.hasAttribute("hidden"),
    hasMountedContent: $cell.$content != null && $cell.$content.parentElement === $cell,
    contentType: $cell.contentType ?? null,
    stacked: $cell.$content instanceof CellGroup,
  }, {
    hasLayout: plan.layout != null,
    skipContent: plan.skipContent,
    contentType: binding?.contentType ?? null,
    stacked: plan.stacking != null,
  });
}
